import * as React from 'react';
import { graphql } from 'react-apollo';

import gql from 'graphql-tag';
import { compose, mapProps } from 'recompose';

import { tupleToStringKey } from 'js/lib/stringKeyTuple';
import waitForGraphQL from 'js/lib/waitForGraphQL';

import { withLoading, withRenderNothing } from 'bundles/coursera-ui/components/hocs/withBranches';
import AboutS12nCourseListItem from 'bundles/coursera-ui/components/rich/MiniXDP/AboutS12nCourseListItem';
import type { Props as MiniSDPProps } from 'bundles/coursera-ui/components/rich/MiniXDP/MiniSDP';
import MiniSDP from 'bundles/coursera-ui/components/rich/MiniXDP/MiniSDP';
import type {
  CuiEnterpriseProgramSessionAssociationsQuery_EnterpriseProgramSessionAssociationsV1Resource_byProgramAndCourses_elements as CourseSession,
  CuiEnterpriseProgramSessionAssociationsQuery as EnterpriseProgramSessionAssociationsQueryData,
  CuiEnterpriseProgramSessionAssociationsQueryVariables as EnterpriseProgramSessionAssociationsQueryVariables,
} from 'bundles/coursera-ui/components/rich/MiniXDP/__generated__/CuiEnterpriseProgramSessionAssociationsQuery';
import type { MiniSDPQuery_XdpV1Resource_get_xdpMetadata_XdpV1_sdpMetadataMember_sdpMetadata_courses as Course } from 'bundles/coursera-ui/components/rich/MiniXDP/__generated__/MiniSDPQuery';
import type { EnterpriseProductMetadataFlags } from 'bundles/enterprise-legacy-learner-home/types/programCommon';
import { PartnerIncentivesSdpFragment } from 'bundles/learner-incentives/components/LearnerIncentive';
import { adaptEnterpriseProductsWithLabs } from 'bundles/nostos-external/adapters';
import { NOSTOS_EXTERNAL_TABLES } from 'bundles/nostos-external/constants';
import type {
  NostosExternalGetDataQueryType,
  NostosExternalGetDataQueryVariables,
} from 'bundles/nostos-external/queries';
import { NostosExternalGetDataQuery } from 'bundles/nostos-external/queries';

type PropsFromCaller = Omit<MiniSDPProps, 'htmlAttributes' | 's12n'> & {
  id: string;
  programId?: string;
  orgId?: string;
  enableCurriculumBuilder?: boolean;
  coursesMetadata?: EnterpriseProductMetadataFlags[];
};

type PropsFromMiniSDPQuery = {
  s12n: any;
};

type PropsFromEnterpriseProgramSessionAssociationsQuery = {
  privateS12nSessionDates?: (CourseSession | null)[];
};

type PropsFromHasCourseraLabsQuery = {
  hasLabAssignments?: boolean;
};

type Props = PropsFromCaller &
  PropsFromMiniSDPQuery &
  PropsFromEnterpriseProgramSessionAssociationsQuery &
  PropsFromHasCourseraLabsQuery;

export const MiniSDPContainer = ({ s12n, enableCurriculumBuilder, coursesMetadata, ...rest }: Props) => {
  return (
    <MiniSDP
      s12n={s12n}
      htmlAttributes={{ 'data-e2e': 'MiniSDP' }}
      enableCurriculumBuilder={enableCurriculumBuilder}
      productMetadata={coursesMetadata}
      {...rest}
    />
  );
};

export const MiniSDPQuery = gql`
  query MiniSDPQuery($id: String!) {
    XdpV1Resource {
      get(id: $id) {
        id
        productId
        productType
        xdpMetadata {
          __typename
          ... on XdpV1_sdpMetadataMember {
            sdpMetadata {
              id
              name
              slug
              productVariant
              description
              avgLearningHoursPerWeek
              avgLearningMonthsAdjusted
              totalEnrollmentCount
              instructors {
                id
                firstName
                lastName
                fullName
                title
              }
              headerImage
              certificateLogo
              companyLogos
              cmlDescription {
                __typename
                ... on XdpV1_cmlMember {
                  cml {
                    value
                    dtdId
                    __typename
                  }
                }
              }
              skills
              s12nAndCourseSkills
              primaryLanguages
              recommendedBackground
              subtitleLanguages
              headline
              subheader
              headerImage
              ratings {
                averageFiveStarRating
                ratingCount
              }
              level
              learningObjectives
              companyLogos
              partners {
                id
                name
                shortName
                landingPageBanner
                description
                partnerMarketingBlurb
                classLogo
                logo
                squareLogo
                rectangularLogo
                primaryColor
                productBrandingLogo
              }
              courseIds
              capstone {
                courseIds
              }
              courses {
                ...AboutS12nCourseListItemFragment
              }
              ...PartnerIncentivesSdpFragment
            }
          }
        }
      }
    }
  }
  ${AboutS12nCourseListItem.fragments.course}
  ${PartnerIncentivesSdpFragment}
`;

const EnterpriseProgramSessionAssociationsQuery = gql`
  query CuiEnterpriseProgramSessionAssociationsQuery($programId: String!, $courseIds: [String!]!) {
    EnterpriseProgramSessionAssociationsV1Resource {
      byProgramAndCourses(programId: $programId, courseIds: $courseIds) {
        elements {
          id
          courseId
          session {
            id
            startsAt
            endsAt
          }
        }
      }
    }
  }
`;

export default compose<Props, PropsFromCaller>(
  // @ts-expect-error TODO type it
  mapProps(({ id, ...rest }) => ({ ...rest, id: tupleToStringKey(['SPECIALIZATION', id]) })),
  graphql(MiniSDPQuery, {
    options: () => ({
      errorPolicy: 'all',
    }),
  }),
  withLoading,
  // @ts-expect-error TODO type it
  mapProps(({ data, ...rest }) => {
    if (!data.XdpV1Resource || !data.XdpV1Resource.get) {
      return { shouldRenderNothing: true };
    }

    return {
      ...rest,
      s12n: {
        ...data.XdpV1Resource.get,
        ...data.XdpV1Resource.get.xdpMetadata.sdpMetadata,
        courseCount: data.XdpV1Resource.get.xdpMetadata.sdpMetadata.courses.length,
      },
    };
  }),
  waitForGraphQL<
    { programId: string; orgId: string } & PropsFromMiniSDPQuery,
    EnterpriseProgramSessionAssociationsQueryData,
    EnterpriseProgramSessionAssociationsQueryVariables,
    PropsFromEnterpriseProgramSessionAssociationsQuery
  >(EnterpriseProgramSessionAssociationsQuery, {
    skip: ({ programId, s12n, orgId }) => !programId || !s12n.courseCount || !orgId,
    options: ({ programId, s12n }) => ({
      variables: { programId, courseIds: s12n.courses.map((course: Course) => course.id) },
    }),
    props: ({ data }) => {
      const privateS12nSessionDates =
        data?.EnterpriseProgramSessionAssociationsV1Resource?.byProgramAndCourses?.elements ?? [];
      return { privateS12nSessionDates };
    },
  }),
  graphql<
    PropsFromMiniSDPQuery,
    NostosExternalGetDataQueryType,
    NostosExternalGetDataQueryVariables,
    PropsFromHasCourseraLabsQuery
  >(NostosExternalGetDataQuery, {
    skip: ({ s12n }) => !s12n,
    options: ({ s12n }) => ({
      variables: {
        input: {
          tableName: NOSTOS_EXTERNAL_TABLES.enterprise_products_with_labs,
          keys: [`SPECIALIZATION~${s12n.id}`],
        },
      },
      context: { clientName: 'gatewayGql' },
      // Matches the `useNostosExternalData` invariant: keep partial data when the
      // gateway also returns errors, so a miss on one key cannot discard the rest.
      errorPolicy: 'all',
    }),
    props: ({ data }) => {
      const elements = adaptEnterpriseProductsWithLabs(data?.NostosExternal?.getData?.entries);
      return {
        hasLabAssignments: elements?.[0]?.content?.hasLabAssignments,
      };
    },
  }),
  withRenderNothing
)(MiniSDPContainer);
